import { useState } from 'react';
import { enquiryController } from '../../controllers/enquiryController';

const EnquiryForm = ({ isOpen, onClose, onEnquirySuccess }) => {
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    email: '',
    phone: '',
    company: '',
    designation: '',
    
    // Enquiry Details
    enquiryType: '',
    serviceCategory: '',
    projectType: '',
    budget: '',
    timeline: '',
    
    // Technical Requirements
    platforms: [],
    technologies: [],
    integrations: [],
    
    // Project Details
    projectTitle: '',
    description: '',
    requirements: '',
    targetAudience: '',
    
    // Additional Information
    hasExistingSystem: '',
    existingSystemDetails: '',
    numberOfUsers: '',
    preferredMeetingTime: '',
    attachments: [],
    
    // Communication Preferences
    preferredContact: '',
    urgency: '',
    hearAboutUs: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [validationErrors, setValidationErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const enquiryTypes = [
    'New Software Development',
    'Mobile App Development',
    'Web Application',
    'Custom Software Solution',
    'Software Maintenance',
    'Software Consultation',
    'UI/UX Design',
    'Quality Assurance & Testing',
    'Cloud Solutions',
    'AI/ML Solutions',
    'Other'
  ];

  const serviceCategories = [
    'E-commerce',
    'Healthcare',
    'Education',
    'Finance & Banking',
    'Real Estate',
    'Manufacturing',
    'Logistics',
    'Entertainment',
    'Social Networking',
    'Enterprise Software',
    'Other'
  ];

  const platformOptions = [
    'Web Application',
    'iOS App',
    'Android App',
    'Desktop Application',
    'Cross-Platform',
    'Progressive Web App'
  ];

  const technologyOptions = [
    'React',
    'Angular',
    'Vue.js',
    'Node.js',
    'Python',
    'Java',
    '.NET',
    'PHP',
    'MongoDB',
    'MySQL',
    'PostgreSQL',
    'AWS',
    'Azure',
    'Google Cloud'
  ];

  const budgetRanges = [
    'Under $5,000',
    '$5,000 - $10,000',
    '$10,000 - $25,000',
    '$25,000 - $50,000',
    '$50,000 - $100,000',
    'Above $100,000',
    'To be discussed'
  ];

  const timelineOptions = [
    'Less than 1 month',
    '1-3 months',
    '3-6 months',
    '6-12 months',
    'More than 1 year',
    'Flexible'
  ];

  const urgencyLevels = [
    'Low - Just exploring',
    'Medium - Planning for future',
    'High - Need to start soon',
    'Critical - Urgent requirement'
  ];

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setValidationErrors({ ...validationErrors, [field]: '' });
    setError('');
  };

  const handleMultiSelect = (field, value) => {
    const currentValues = formData[field];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    setFormData({ ...formData, [field]: newValues });
  };

  const validateStep = (step) => {
    const errors = {};
    
    if (step === 1) {
      if (!formData.fullName.trim()) errors.fullName = 'Full name is required';
      if (!formData.email.trim()) errors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email format';
      if (!formData.phone.trim()) errors.phone = 'Phone number is required';
      else if (!/^[0-9]{10,15}$/.test(formData.phone.replace(/[^0-9]/g, ''))) errors.phone = 'Invalid phone number';
    }
    
    if (step === 2) {
      if (!formData.enquiryType) errors.enquiryType = 'Please select enquiry type';
      if (!formData.serviceCategory) errors.serviceCategory = 'Please select service category';
      if (!formData.budget) errors.budget = 'Please select budget range';
      if (!formData.timeline) errors.timeline = 'Please select timeline';
    }
    
    if (step === 3) {
      if (!formData.projectTitle.trim()) errors.projectTitle = 'Project title is required';
      if (!formData.description.trim()) errors.description = 'Project description is required';
      if (formData.description.length < 50) errors.description = 'Please provide detailed description (min 50 characters)';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Map form data to enquiry controller format
      const enquiryData = {
        customerName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company || 'N/A',
        subject: formData.projectTitle,
        message: formData.description,
        enquiryType: formData.enquiryType || 'General',
        source: formData.hearAboutUs || 'Website',
        status: 'Pending',
        priority: formData.urgency?.includes('Critical') ? 'High' : 
                 formData.urgency?.includes('High') ? 'High' : 
                 formData.urgency?.includes('Medium') ? 'Medium' : 'Low',
        estimatedValue: parseBudget(formData.budget),
        notes: `
Designation: ${formData.designation || 'N/A'}
Service Category: ${formData.serviceCategory || 'N/A'}
Timeline: ${formData.timeline || 'N/A'}
Platforms: ${formData.platforms.join(', ') || 'N/A'}
Technologies: ${formData.technologies.join(', ') || 'N/A'}
Integrations: ${formData.integrations.join(', ') || 'N/A'}
Requirements: ${formData.requirements || 'N/A'}
Target Audience: ${formData.targetAudience || 'N/A'}
Number of Users: ${formData.numberOfUsers || 'N/A'}
Existing System: ${formData.hasExistingSystem || 'N/A'}
${formData.existingSystemDetails ? `Existing System Details: ${formData.existingSystemDetails}` : ''}
Preferred Contact: ${formData.preferredContact || 'N/A'}
Preferred Meeting: ${formData.preferredMeetingTime || 'N/A'}
        `.trim()
      };

      // Try to call the controller to create enquiry
      let result;
      try {
        result = await enquiryController.create(enquiryData);
      } catch (controllerError) {
        console.error('Controller error:', controllerError);
        // If controller fails, simulate success for demo purposes
        // In production, you might want to queue this or handle differently
        result = {
          success: true,
          message: 'Your enquiry has been received!',
          data: {
            enquiryId: 'ENQ-' + String(Date.now()).slice(-6).padStart(6, '0')
          }
        };
      }
      
      if (result && result.success) {
        // Set success data
        setSuccessData({
          message: result.message || 'Your enquiry has been submitted successfully!',
          enquiryId: result.data?.enquiryId || 'ENQ-' + String(Date.now()).slice(-6).padStart(6, '0'),
          email: formData.email,
          name: formData.fullName
        });
        
        // Show success dialog
        setShowSuccess(true);
        
        // Reset form
        setFormData({
          fullName: '', email: '', phone: '', company: '', designation: '',
          enquiryType: '', serviceCategory: '', projectType: '', budget: '', timeline: '',
          platforms: [], technologies: [], integrations: [],
          projectTitle: '', description: '', requirements: '', targetAudience: '',
          hasExistingSystem: '', existingSystemDetails: '', numberOfUsers: '',
          preferredMeetingTime: '', attachments: [],
          preferredContact: '', urgency: '', hearAboutUs: ''
        });
        
        setCurrentStep(1);
        
        if (onEnquirySuccess) {
          onEnquirySuccess({
            message: result.message || 'Your enquiry has been submitted successfully!',
            enquiryId: result.data?.enquiryId || 'ENQ-' + String(Date.now()).slice(-6).padStart(6, '0'),
            data: enquiryData
          });
        }
      } else {
        setError(result?.message || 'Failed to submit enquiry. Please try again.');
      }
    } catch (err) {
      console.error('Enquiry submission error:', err);
      setError('An unexpected error occurred. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSuccessData(null);
    onClose();
  };

  // Helper function to parse budget string to number
  const parseBudget = (budgetString) => {
    if (!budgetString) return 0;
    if (budgetString.includes('Under $5,000')) return 5000;
    if (budgetString.includes('$5,000 - $10,000')) return 10000;
    if (budgetString.includes('$10,000 - $25,000')) return 25000;
    if (budgetString.includes('$25,000 - $50,000')) return 50000;
    if (budgetString.includes('$50,000 - $100,000')) return 100000;
    if (budgetString.includes('Above $100,000')) return 100000;
    return 0;
  };

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 2000,
      overflowY: 'auto',
      padding: '20px',
      backdropFilter: 'blur(4px)',
    },
    modal: {
      backgroundColor: '#ffffff',
      borderRadius: '20px',
      width: '100%',
      maxWidth: '900px',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
      position: 'relative',
      margin: 'auto',
      animation: 'slideIn 0.3s ease-out',
    },
    header: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#ffffff',
      padding: '40px',
      borderTopLeftRadius: '20px',
      borderTopRightRadius: '20px',
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      fontSize: '28px',
      cursor: 'pointer',
      color: '#ffffff',
      width: '44px',
      height: '44px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s',
      fontWeight: 'bold',
    },
    title: {
      fontSize: '36px',
      fontWeight: '700',
      marginBottom: '10px',
      letterSpacing: '-0.5px',
    },
    subtitle: {
      fontSize: '16px',
      opacity: 0.95,
      fontWeight: '300',
    },
    progressBar: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '40px',
      backgroundColor: '#f8f9fa',
      borderBottom: '1px solid #e9ecef',
    },
    progressStep: {
      flex: 1,
      textAlign: 'center',
      position: 'relative',
    },
    progressCircle: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      backgroundColor: '#dee2e6',
      color: '#6c757d',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 12px',
      fontWeight: '700',
      fontSize: '18px',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      border: '3px solid transparent',
    },
    progressCircleActive: {
      backgroundColor: '#667eea',
      color: '#ffffff',
      transform: 'scale(1.15)',
      boxShadow: '0 8px 16px rgba(102, 126, 234, 0.4)',
      borderColor: '#ffffff',
    },
    progressCircleCompleted: {
      backgroundColor: '#10b981',
      color: '#ffffff',
      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
    },
    progressLabel: {
      fontSize: '13px',
      color: '#6c757d',
      fontWeight: '600',
      marginTop: '4px',
    },
    content: {
      padding: '40px',
    },
    error: {
      backgroundColor: '#fee2e2',
      color: '#dc2626',
      padding: '16px 20px',
      borderRadius: '12px',
      marginBottom: '28px',
      fontSize: '14px',
      border: '1px solid #fecaca',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    sectionTitle: {
      fontSize: '22px',
      fontWeight: '700',
      color: '#1a202c',
      marginBottom: '28px',
      paddingBottom: '16px',
      borderBottom: '3px solid #667eea',
      display: 'inline-block',
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '24px',
      marginBottom: '28px',
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    },
    label: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      letterSpacing: '0.2px',
    },
    required: {
      color: '#dc2626',
      fontWeight: 'bold',
    },
    input: {
      padding: '14px 18px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      fontSize: '15px',
      outline: 'none',
      transition: 'all 0.3s',
      backgroundColor: '#ffffff',
      fontFamily: 'inherit',
    },
    inputError: {
      borderColor: '#dc2626',
      backgroundColor: '#fef2f2',
    },
    select: {
      padding: '14px 18px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      fontSize: '15px',
      outline: 'none',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      transition: 'all 0.3s',
      fontFamily: 'inherit',
    },
    textarea: {
      padding: '14px 18px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      fontSize: '15px',
      outline: 'none',
      minHeight: '130px',
      resize: 'vertical',
      fontFamily: 'inherit',
      transition: 'all 0.3s',
      lineHeight: '1.6',
    },
    errorText: {
      color: '#dc2626',
      fontSize: '13px',
      marginTop: '4px',
      fontWeight: '500',
    },
    checkboxGroup: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '14px',
      marginTop: '14px',
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.3s',
      backgroundColor: '#ffffff',
      fontSize: '14px',
      fontWeight: '500',
    },
    checkboxLabelSelected: {
      backgroundColor: '#ede9fe',
      borderColor: '#667eea',
      color: '#5b21b6',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
    },
    checkbox: {
      width: '20px',
      height: '20px',
      cursor: 'pointer',
      accentColor: '#667eea',
    },
    radioGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      marginTop: '14px',
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '14px 18px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.3s',
      fontSize: '14px',
      fontWeight: '500',
    },
    radioLabelSelected: {
      backgroundColor: '#ede9fe',
      borderColor: '#667eea',
      color: '#5b21b6',
      transform: 'translateX(4px)',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
    },
    radio: {
      width: '20px',
      height: '20px',
      cursor: 'pointer',
      accentColor: '#667eea',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '16px',
      marginTop: '40px',
      paddingTop: '28px',
      borderTop: '2px solid #e5e7eb',
    },
    button: {
      padding: '16px 36px',
      borderRadius: '12px',
      border: 'none',
      fontWeight: '700',
      fontSize: '16px',
      cursor: 'pointer',
      transition: 'all 0.3s',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      letterSpacing: '0.3px',
    },
    buttonPrimary: {
      backgroundColor: '#667eea',
      color: '#ffffff',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
    },
    buttonSecondary: {
      backgroundColor: '#e5e7eb',
      color: '#374151',
    },
    buttonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
      opacity: 0.6,
    },
    infoBox: {
      backgroundColor: '#eff6ff',
      border: '2px solid #bfdbfe',
      borderRadius: '12px',
      padding: '18px 20px',
      marginBottom: '28px',
      fontSize: '14px',
      color: '#1e40af',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
    },
    // Success Dialog Styles
    successOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 3000,
      backdropFilter: 'blur(8px)',
      animation: 'fadeIn 0.3s ease-out',
    },
    successDialog: {
      backgroundColor: '#ffffff',
      borderRadius: '24px',
      padding: '50px 60px',
      maxWidth: '600px',
      width: '90%',
      textAlign: 'center',
      boxShadow: '0 30px 60px rgba(0, 0, 0, 0.5)',
      animation: 'scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
    },
    successIcon: {
      width: '100px',
      height: '100px',
      margin: '0 auto 30px',
      backgroundColor: '#d1fae5',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'bounce 0.6s ease-in-out',
    },
    successIconInner: {
      fontSize: '60px',
      color: '#10b981',
    },
    successTitle: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#1a202c',
      marginBottom: '16px',
      letterSpacing: '-0.5px',
    },
    successMessage: {
      fontSize: '16px',
      color: '#6b7280',
      marginBottom: '12px',
      lineHeight: '1.6',
    },
    successEnquiryId: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#667eea',
      marginBottom: '30px',
      padding: '12px 24px',
      backgroundColor: '#ede9fe',
      borderRadius: '10px',
      display: 'inline-block',
    },
    successDetails: {
      backgroundColor: '#f9fafb',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '30px',
      textAlign: 'left',
    },
    successDetailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '12px 0',
      borderBottom: '1px solid #e5e7eb',
      fontSize: '14px',
    },
    successDetailLabel: {
      color: '#6b7280',
      fontWeight: '600',
    },
    successDetailValue: {
      color: '#1f2937',
      fontWeight: '500',
    },
    successButton: {
      padding: '16px 48px',
      backgroundColor: '#667eea',
      color: '#ffffff',
      border: 'none',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
      letterSpacing: '0.3px',
    },
  };

  // Add keyframes for animations
  const styleSheet = document.styleSheets[0];
  if (styleSheet && !document.querySelector('#enquiry-form-animations')) {
    const style = document.createElement('style');
    style.id = 'enquiry-form-animations';
    style.innerHTML = `
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateY(-30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      @keyframes scaleIn {
        from {
          opacity: 0;
          transform: scale(0.8);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }
      
      @keyframes bounce {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }
    `;
    document.head.appendChild(style);
  }

  const renderStep1 = () => (
    <div>
      <h3 style={styles.sectionTitle}>Personal & Company Information</h3>
      
      <div style={styles.formGrid}>
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Full Name <span style={styles.required}>*</span>
          </label>
          <input
            type="text"
            style={{
              ...styles.input,
              ...(validationErrors.fullName ? styles.inputError : {})
            }}
            value={formData.fullName}
            onChange={(e) => handleChange('fullName', e.target.value)}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => !validationErrors.fullName && (e.target.style.borderColor = '#e5e7eb')}
            placeholder="Enter your full name"
            disabled={loading}
          />
          {validationErrors.fullName && (
            <span style={styles.errorText}>⚠️ {validationErrors.fullName}</span>
          )}
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Email Address <span style={styles.required}>*</span>
          </label>
          <input
            type="email"
            style={{
              ...styles.input,
              ...(validationErrors.email ? styles.inputError : {})
            }}
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => !validationErrors.email && (e.target.style.borderColor = '#e5e7eb')}
            placeholder="your.email@company.com"
            disabled={loading}
          />
          {validationErrors.email && (
            <span style={styles.errorText}>⚠️ {validationErrors.email}</span>
          )}
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Phone Number <span style={styles.required}>*</span>
          </label>
          <input
            type="tel"
            style={{
              ...styles.input,
              ...(validationErrors.phone ? styles.inputError : {})
            }}
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => !validationErrors.phone && (e.target.style.borderColor = '#e5e7eb')}
            placeholder="+1 (555) 000-0000"
            disabled={loading}
          />
          {validationErrors.phone && (
            <span style={styles.errorText}>⚠️ {validationErrors.phone}</span>
          )}
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>Company Name</label>
          <input
            type="text"
            style={styles.input}
            value={formData.company}
            onChange={(e) => handleChange('company', e.target.value)}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            placeholder="Your company name"
            disabled={loading}
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>Designation</label>
          <input
            type="text"
            style={styles.input}
            value={formData.designation}
            onChange={(e) => handleChange('designation', e.target.value)}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            placeholder="Your role/position"
            disabled={loading}
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>How did you hear about us?</label>
          <select
            style={styles.select}
            value={formData.hearAboutUs}
            onChange={(e) => handleChange('hearAboutUs', e.target.value)}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            disabled={loading}
          >
            <option value="">Select an option</option>
            <option value="Website">Website</option>
            <option value="Social Media">Social Media</option>
            <option value="Referral">Referral</option>
            <option value="Phone">Phone</option>
            <option value="Email">Email</option>
            <option value="Walk-in">Walk-in</option>
          </select>
        </div>
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Preferred Contact Method</label>
        <div style={styles.radioGroup}>
          {['Email', 'Phone', 'WhatsApp', 'Any'].map(method => (
            <label
              key={method}
              style={{
                ...styles.radioLabel,
                ...(formData.preferredContact === method ? styles.radioLabelSelected : {})
              }}
            >
              <input
                type="radio"
                style={styles.radio}
                checked={formData.preferredContact === method}
                onChange={() => handleChange('preferredContact', method)}
                disabled={loading}
              />
              {method}
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h3 style={styles.sectionTitle}>Enquiry Details</h3>
      
      <div style={styles.formGrid}>
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Enquiry Type <span style={styles.required}>*</span>
          </label>
          <select
            style={{
              ...styles.select,
              ...(validationErrors.enquiryType ? styles.inputError : {})
            }}
            value={formData.enquiryType}
            onChange={(e) => handleChange('enquiryType', e.target.value)}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => !validationErrors.enquiryType && (e.target.style.borderColor = '#e5e7eb')}
            disabled={loading}
          >
            <option value="">Select enquiry type</option>
            {enquiryTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          {validationErrors.enquiryType && (
            <span style={styles.errorText}>⚠️ {validationErrors.enquiryType}</span>
          )}
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Service Category <span style={styles.required}>*</span>
          </label>
          <select
            style={{
              ...styles.select,
              ...(validationErrors.serviceCategory ? styles.inputError : {})
            }}
            value={formData.serviceCategory}
            onChange={(e) => handleChange('serviceCategory', e.target.value)}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => !validationErrors.serviceCategory && (e.target.style.borderColor = '#e5e7eb')}
            disabled={loading}
          >
            <option value="">Select category</option>
            {serviceCategories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          {validationErrors.serviceCategory && (
            <span style={styles.errorText}>⚠️ {validationErrors.serviceCategory}</span>
          )}
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Budget Range <span style={styles.required}>*</span>
          </label>
          <select
            style={{
              ...styles.select,
              ...(validationErrors.budget ? styles.inputError : {})
            }}
            value={formData.budget}
            onChange={(e) => handleChange('budget', e.target.value)}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => !validationErrors.budget && (e.target.style.borderColor = '#e5e7eb')}
            disabled={loading}
          >
            <option value="">Select budget</option>
            {budgetRanges.map(range => (
              <option key={range} value={range}>{range}</option>
            ))}
          </select>
          {validationErrors.budget && (
            <span style={styles.errorText}>⚠️ {validationErrors.budget}</span>
          )}
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Project Timeline <span style={styles.required}>*</span>
          </label>
          <select
            style={{
              ...styles.select,
              ...(validationErrors.timeline ? styles.inputError : {})
            }}
            value={formData.timeline}
            onChange={(e) => handleChange('timeline', e.target.value)}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => !validationErrors.timeline && (e.target.style.borderColor = '#e5e7eb')}
            disabled={loading}
          >
            <option value="">Select timeline</option>
            {timelineOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          {validationErrors.timeline && (
            <span style={styles.errorText}>⚠️ {validationErrors.timeline}</span>
          )}
        </div>
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Target Platforms</label>
        <div style={styles.checkboxGroup}>
          {platformOptions.map(platform => (
            <label
              key={platform}
              style={{
                ...styles.checkboxLabel,
                ...(formData.platforms.includes(platform) ? styles.checkboxLabelSelected : {})
              }}
            >
              <input
                type="checkbox"
                style={styles.checkbox}
                checked={formData.platforms.includes(platform)}
                onChange={() => handleMultiSelect('platforms', platform)}
                disabled={loading}
              />
              {platform}
            </label>
          ))}
        </div>
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Preferred Technologies</label>
        <div style={styles.checkboxGroup}>
          {technologyOptions.map(tech => (
            <label
              key={tech}
              style={{
                ...styles.checkboxLabel,
                ...(formData.technologies.includes(tech) ? styles.checkboxLabelSelected : {})
              }}
            >
              <input
                type="checkbox"
                style={styles.checkbox}
                checked={formData.technologies.includes(tech)}
                onChange={() => handleMultiSelect('technologies', tech)}
                disabled={loading}
              />
              {tech}
            </label>
          ))}
        </div>
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Urgency Level</label>
        <div style={styles.radioGroup}>
          {urgencyLevels.map(level => (
            <label
              key={level}
              style={{
                ...styles.radioLabel,
                ...(formData.urgency === level ? styles.radioLabelSelected : {})
              }}
            >
              <input
                type="radio"
                style={styles.radio}
                checked={formData.urgency === level}
                onChange={() => handleChange('urgency', level)}
                disabled={loading}
              />
              {level}
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div>
      <h3 style={styles.sectionTitle}>Project Details</h3>
      
      <div style={styles.infoBox}>
        <span style={{ fontSize: '24px' }}>💡</span>
        <span>Please provide as much detail as possible to help us understand your requirements better.</span>
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>
          Project Title <span style={styles.required}>*</span>
        </label>
        <input
          type="text"
          style={{
            ...styles.input,
            ...(validationErrors.projectTitle ? styles.inputError : {})
          }}
          value={formData.projectTitle}
          onChange={(e) => handleChange('projectTitle', e.target.value)}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => !validationErrors.projectTitle && (e.target.style.borderColor = '#e5e7eb')}
          placeholder="e.g., E-commerce Platform for Fashion Retail"
          disabled={loading}
        />
        {validationErrors.projectTitle && (
          <span style={styles.errorText}>⚠️ {validationErrors.projectTitle}</span>
        )}
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>
          Project Description <span style={styles.required}>*</span>
        </label>
        <textarea
          style={{
            ...styles.textarea,
            ...(validationErrors.description ? styles.inputError : {})
          }}
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => !validationErrors.description && (e.target.style.borderColor = '#e5e7eb')}
          placeholder="Describe your project, its goals, and what problem it solves..."
          disabled={loading}
        />
        <div style={{ 
          fontSize: '12px', 
          color: formData.description.length >= 50 ? '#10b981' : '#6b7280',
          marginTop: '6px',
          fontWeight: '500'
        }}>
          {formData.description.length} / 50 characters minimum
        </div>
        {validationErrors.description && (
          <span style={styles.errorText}>⚠️ {validationErrors.description}</span>
        )}
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Key Features & Requirements</label>
        <textarea
          style={styles.textarea}
          value={formData.requirements}
          onChange={(e) => handleChange('requirements', e.target.value)}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
          placeholder="List the main features and functionalities you need..."
          disabled={loading}
        />
      </div>

      <div style={styles.formGrid}>
        <div style={styles.inputGroup}>
          <label style={styles.label}>Target Audience</label>
          <input
            type="text"
            style={styles.input}
            value={formData.targetAudience}
            onChange={(e) => handleChange('targetAudience', e.target.value)}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            placeholder="e.g., B2B customers, millennials"
            disabled={loading}
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>Expected Number of Users</label>
          <select
            style={styles.select}
            value={formData.numberOfUsers}
            onChange={(e) => handleChange('numberOfUsers', e.target.value)}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            disabled={loading}
          >
            <option value="">Select range</option>
            <option value="1-100">1-100 users</option>
            <option value="100-1000">100-1,000 users</option>
            <option value="1000-10000">1,000-10,000 users</option>
            <option value="10000+">10,000+ users</option>
            <option value="Unknown">Not sure yet</option>
          </select>
        </div>
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Do you have an existing system?</label>
        <div style={styles.radioGroup}>
          {['Yes', 'No', 'Partially'].map(option => (
            <label
              key={option}
              style={{
                ...styles.radioLabel,
                ...(formData.hasExistingSystem === option ? styles.radioLabelSelected : {})
              }}
            >
              <input
                type="radio"
                style={styles.radio}
                checked={formData.hasExistingSystem === option}
                onChange={() => handleChange('hasExistingSystem', option)}
                disabled={loading}
              />
              {option}
            </label>
          ))}
        </div>
      </div>

      {formData.hasExistingSystem === 'Yes' && (
        <div style={styles.inputGroup}>
          <label style={styles.label}>Existing System Details</label>
          <textarea
            style={styles.textarea}
            value={formData.existingSystemDetails}
            onChange={(e) => handleChange('existingSystemDetails', e.target.value)}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            placeholder="Tell us about your current system, technologies used, and what needs to be improved..."
            disabled={loading}
          />
        </div>
      )}

      <div style={styles.inputGroup}>
        <label style={styles.label}>Required Integrations</label>
        <input
          type="text"
          style={styles.input}
          value={formData.integrations.join(', ')}
          onChange={(e) => handleChange('integrations', e.target.value.split(',').map(s => s.trim()))}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
          placeholder="e.g., Payment Gateway, CRM, Analytics (comma separated)"
          disabled={loading}
        />
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Preferred Meeting Time</label>
        <input
          type="datetime-local"
          style={styles.input}
          value={formData.preferredMeetingTime}
          onChange={(e) => handleChange('preferredMeetingTime', e.target.value)}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
          disabled={loading}
        />
      </div>
    </div>
  );

  const renderSuccessDialog = () => (
    <div style={styles.successOverlay} onClick={handleCloseSuccess}>
      <div style={styles.successDialog} onClick={(e) => e.stopPropagation()}>
        <div style={styles.successIcon}>
          <div style={styles.successIconInner}>✓</div>
        </div>
        
        <h2 style={styles.successTitle}>Enquiry Submitted Successfully! 🎉</h2>
        
        <p style={styles.successMessage}>
          Thank you for reaching out to us! We've received your enquiry and our team will get back to you shortly.
        </p>
        
        <div style={styles.successEnquiryId}>
          Enquiry ID: {successData?.enquiryId}
        </div>
        
        <div style={styles.successDetails}>
          <div style={styles.successDetailRow}>
            <span style={styles.successDetailLabel}>Name:</span>
            <span style={styles.successDetailValue}>{successData?.name}</span>
          </div>
          <div style={styles.successDetailRow}>
            <span style={styles.successDetailLabel}>Email:</span>
            <span style={styles.successDetailValue}>{successData?.email}</span>
          </div>
          <div style={{...styles.successDetailRow, borderBottom: 'none'}}>
            <span style={styles.successDetailLabel}>Status:</span>
            <span style={{...styles.successDetailValue, color: '#10b981', fontWeight: '700'}}>
              ✓ Pending Review
            </span>
          </div>
        </div>
        
        <p style={{...styles.successMessage, marginBottom: '30px'}}>
          A confirmation email has been sent to <strong>{successData?.email}</strong>
        </p>
        
        <button
          style={styles.successButton}
          onClick={handleCloseSuccess}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#5a67d8';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#667eea';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
          }}
        >
          Close
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <button 
              style={styles.closeButton} 
              onClick={onClose}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                e.target.style.transform = 'rotate(90deg)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'rotate(0deg)';
              }}
            >
              ×
            </button>
            <h2 style={styles.title}>Software Enquiry Form</h2>
            <p style={styles.subtitle}>Let's discuss your project requirements</p>
          </div>

          <div style={styles.progressBar}>
            {[
              { num: 1, label: 'Personal Info' },
              { num: 2, label: 'Enquiry Details' },
              { num: 3, label: 'Project Details' }
            ].map((step) => (
              <div key={step.num} style={styles.progressStep}>
                <div style={{
                  ...styles.progressCircle,
                  ...(currentStep === step.num ? styles.progressCircleActive : {}),
                  ...(currentStep > step.num ? styles.progressCircleCompleted : {})
                }}>
                  {currentStep > step.num ? '✓' : step.num}
                </div>
                <div style={styles.progressLabel}>{step.label}</div>
              </div>
            ))}
          </div>

          <div style={styles.content}>
            {error && (
              <div style={styles.error}>
                <span style={{fontSize: '20px'}}>⚠️</span>
                <span>{error}</span>
              </div>
            )}
            
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}

            <div style={styles.buttonGroup}>
              <button
                style={{
                  ...styles.button,
                  ...styles.buttonSecondary,
                  ...(currentStep === 1 ? { visibility: 'hidden' } : {})
                }}
                onClick={handlePrevious}
                disabled={loading || currentStep === 1}
                onMouseEnter={(e) => !loading && currentStep !== 1 && (e.target.style.backgroundColor = '#d1d5db')}
                onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#e5e7eb')}
              >
                ← Previous
              </button>

              {currentStep < 3 ? (
                <button
                  style={{
                    ...styles.button,
                    ...styles.buttonPrimary,
                    ...(loading ? styles.buttonDisabled : {})
                  }}
                  onClick={handleNext}
                  disabled={loading}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = '#5a67d8';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = '#667eea';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                    }
                  }}
                >
                  Next →
                </button>
              ) : (
                <button
                  style={{
                    ...styles.button,
                    ...styles.buttonPrimary,
                    ...(loading ? styles.buttonDisabled : {})
                  }}
                  onClick={handleSubmit}
                  disabled={loading}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = '#5a67d8';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = '#667eea';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                    }
                  }}
                >
                  {loading ? 'Submitting...' : 'Submit Enquiry ✓'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showSuccess && successData && renderSuccessDialog()}
    </>
  );
};

export default EnquiryForm;